/**
 * Solana Extra Token Integration Helpers
 * Provides parity with BNB ERC20 extra token functionality
 */

import {
  Connection,
  PublicKey,
  Transaction,
} from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction,
  TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { Buffer } from 'buffer';

/**
 * Distribution result interface
 */
export interface DistributionResult {
  tokensPerReceiver: number;
  localJackpot: number;
  totalDistributed: number;
}

/**
 * Calculate distribution amounts (BNB parity)
 * Formula: extraTokenAmount * (10000 - localJackpotPercent) / 10000
 *
 * @param totalTokens - Total tokens for all tickets
 * @param numTickets - Number of tickets
 * @param jackpotPercentBps - Jackpot percentage in basis points (e.g., 2000 = 20%)
 * @returns Distribution amounts
 *
 * @example
 * const dist = calculateDistribution(1000, 10, 2000);
 * // { tokensPerReceiver: 80, localJackpot: 200, totalDistributed: 1000 }
 */
export function calculateDistribution(
  totalTokens: number,
  numTickets: number,
  jackpotPercentBps = 2000, // Default 20%
): DistributionResult {
  // Calculate per ticket amount
  const tokensPerTicket = totalTokens / numTickets;

  // Calculate per receiver (80% if jackpot is 20%)
  const tokensPerReceiver = Math.floor(
    (tokensPerTicket * (10000 - jackpotPercentBps)) / 10000,
  );

  // Calculate local jackpot (20%)
  const localJackpot = Math.floor((totalTokens * jackpotPercentBps) / 10000);

  // Verify total
  const totalDistributed = tokensPerReceiver * numTickets + localJackpot;

  return {
    tokensPerReceiver,
    localJackpot,
    totalDistributed,
  };
}

/**
 * Get or create Associated Token Accounts for receivers
 *
 * @param connection - Solana connection
 * @param payer - Payer keypair
 * @param receivers - Array of receiver public keys
 * @param mint - Token mint address
 * @returns Array of ATA addresses
 */
export async function ensureATAs(
  connection: Connection,
  payer: any,
  receivers: PublicKey[],
  mint: PublicKey,
): Promise<PublicKey[]> {
  const instructions: any[] = [];
  const ataPromises = receivers.map(async receiver => {
    const ata = await getAssociatedTokenAddress(mint, receiver);

    // Check if ATA exists
    const ataInfo = await connection.getAccountInfo(ata);

    if (!ataInfo) {
      // Create ATA instruction
      instructions.push(
        createAssociatedTokenAccountInstruction(
          payer.publicKey,
          ata,
          receiver,
          mint,
        ),
      );
    }

    return ata;
  });

  // Wait for all ATAs
  const atas = await Promise.all(ataPromises);

  // Execute create instructions if needed
  if (instructions.length > 0) {
    const tx = new Transaction().add(...instructions);
    // Prepare transaction with recent blockhash for Phantom wallet
    const { blockhash } = await connection.getLatestBlockhash('confirmed');
    tx.recentBlockhash = blockhash;
    tx.feePayer = payer.publicKey;
    // Use wallet.sendTransaction instead of sendAndConfirmTransaction
    // because payer is a wallet object (Phantom), not a Keypair
    const signature = await payer.sendTransaction(tx, connection);
    await connection.confirmTransaction(signature, 'confirmed');
  }

  return atas;
}

/**
 * Transfer tokens to receivers (BNB equivalent)
 *
 * @param connection - Solana connection
 * @param payer - Payer keypair
 * @param buyerATA - Buyer's token account
 * @param receivers - Array of receiver public keys
 * @param receiverATAs - Array of receiver ATAs
 * @param tokensPerReceiver - Amount to transfer to each receiver
 * @param mint - Token mint address
 */
export async function transferToReceivers(
  connection: Connection,
  payer: any,
  buyerATA: PublicKey,
  receivers: PublicKey[],
  receiverATAs: PublicKey[],
  tokensPerReceiver: number,
  mint: PublicKey,
): Promise<string> {
  const instructions: any[] = [];

  // Create transfer instruction for each receiver
  for (let i = 0; i < receivers.length; i++) {
    instructions.push(
      createTransferInstruction(
        buyerATA,
        receiverATAs[i],
        payer.publicKey,
        tokensPerReceiver,
        [],
        TOKEN_PROGRAM_ID,
      ),
    );
  }

  // Execute all transfers
  // Prepare transaction with recent blockhash for Phantom wallet
  const tx = new Transaction().add(...instructions);
  const { blockhash } = await connection.getLatestBlockhash('confirmed');
  tx.recentBlockhash = blockhash;
  tx.feePayer = payer.publicKey;
  // Use wallet.sendTransaction instead of sendAndConfirmTransaction
  // because payer is a wallet object (Phantom), not a Keypair
  const signature = await payer.sendTransaction(tx, connection);
  await connection.confirmTransaction(signature, 'confirmed');

  console.log('✅ Transferred to receivers:', signature);
  return signature;
}

/**
 * Transfer tokens to vault (local jackpot)
 * IMPORTANT: Creates vault ATA if it doesn't exist
 *
 * @param connection - Solana connection
 * @param payer - Payer keypair (wallet)
 * @param buyerATA - Buyer's token account
 * @param vaultATA - Vault token account address
 * @param vaultAuthority - Vault authority PDA (owner of vault ATA)
 * @param mint - Token mint address
 * @param localJackpot - Amount to transfer to vault
 */
export async function transferToVault(
  connection: Connection,
  payer: any,
  buyerATA: PublicKey,
  vaultATA: PublicKey,
  vaultAuthority: PublicKey,
  mint: PublicKey,
  localJackpot: number,
): Promise<string> {
  // Check if vault ATA exists, create if not
  const vaultATAInfo = await connection.getAccountInfo(vaultATA);
  const instructions: any[] = [];
  
  if (!vaultATAInfo) {
    console.log('[transferToVault] Vault ATA does not exist, creating...');
    // Create ATA instruction for vault
    // Note: For off-curve PDA owner, we still need to create ATA manually
    instructions.push(
      createAssociatedTokenAccountInstruction(
        payer.publicKey,  // Payer
        vaultATA,          // ATA address (derived from vaultAuthority)
        vaultAuthority,    // Owner (off-curve PDA)
        mint,              // Mint
      ),
    );
  }

  // Transfer instruction
  const transferIx = createTransferInstruction(
    buyerATA,
    vaultATA,
    payer.publicKey,
    localJackpot,
    [],
    TOKEN_PROGRAM_ID,
  );
  instructions.push(transferIx);

  // Prepare transaction with recent blockhash for Phantom wallet
  const tx = new Transaction().add(...instructions);
  const { blockhash } = await connection.getLatestBlockhash('confirmed');
  tx.recentBlockhash = blockhash;
  tx.feePayer = payer.publicKey;
  
  // Use signAndSendTransaction for Phantom wallet
  let signature: string;
  if (typeof payer.signAndSendTransaction === 'function') {
    const result = await payer.signAndSendTransaction(tx);
    signature = result.signature;
  } else {
    signature = await payer.sendTransaction(tx, connection);
  }
  
  await connection.confirmTransaction(signature, 'confirmed');

  console.log('✅ Transferred to vault:', signature);
  return signature;
}

/**
 * Get vault authority PDA for a token
 * IMPORTANT: Seeds must match Rust program: ["vault", group_id_le, mint]
 *
 * @param programId - Program ID
 * @param groupId - Group ID (u64)
 * @param mint - Token mint address
 * @returns [PDA address, bump]
 */
export function deriveVaultAuthority(
  programId: PublicKey,
  groupId: number | bigint,
  mint: PublicKey,
): [PublicKey, number] {
  // Convert groupId to u64 little-endian bytes
  const groupIdBuffer = Buffer.allocUnsafe(8);
  const view = new DataView(groupIdBuffer.buffer);
  view.setBigUint64(0, BigInt(groupId), true); // little endian
  
  // Seeds: ["vault", groupId (u64 le), mint]
  return PublicKey.findProgramAddressSync(
    [Buffer.from('vault'), groupIdBuffer, mint.toBuffer()],
    programId,
  );
}

/**
 * Complete flow: Buy tickets with extra token
 * This matches BNB's mintLixi() behavior
 *
 * @param params - Parameters
 * @returns Transaction signatures and distribution
 */
export async function createLixiWithExtraToken(params: {
  connection: Connection;
  program: any; // Anchor Program
  buyer: any; // Keypair
  globalState: PublicKey;
  groupId: number;
  numTickets: number;
  extraTokenMint: PublicKey;
  totalTokens: number;
  receivers: PublicKey[];
  jackpotPercentBps?: number;
}): Promise<{
  buyTx: string;
  transferTx: string;
  vaultTx: string;
  distribution: DistributionResult;
}> {
  console.log('🎫 Creating lixi with extra token...');

  // Step 1: Calculate distribution (BNB parity)
  const distribution = calculateDistribution(
    params.totalTokens,
    params.numTickets,
    params.jackpotPercentBps || 2000,
  );

  console.log('📊 Distribution calculated:', distribution);

  // Step 2: Ensure all ATAs exist
  console.log('🔄 Ensuring ATAs exist...');
  await ensureATAs(
    params.connection,
    params.buyer,
    params.receivers,
    params.extraTokenMint,
  );

  // Step 3: Get buyer ATA
  const buyerATA = await getAssociatedTokenAddress(
    params.extraTokenMint,
    params.buyer.publicKey,
  );

  // Step 4: Get vault ATA
  const [vaultAuthority] = deriveVaultAuthority(
    params.program.programId,
    params.groupId,
    params.extraTokenMint,
  );
  const vaultATA = await getAssociatedTokenAddress(
    params.extraTokenMint,
    vaultAuthority,
  );

  // Step 5: Get receiver ATAs
  const receiverATAs = await Promise.all(
    params.receivers.map(receiver =>
      getAssociatedTokenAddress(params.extraTokenMint, receiver),
    ),
  );

  // Step 6: Buy tickets
  console.log('💳 Buying tickets...');
  const buyTx = await params.program.methods
    .buyTickets(
      params.numTickets,
      params.extraTokenMint,
      params.totalTokens,
      params.receivers,
    )
    .accounts({
      globalState: params.globalState,
      // Add other required accounts based on your IDL
    })
    .remainingAccounts([
      { pubkey: buyerATA, isSigner: false, isWritable: true },
      { pubkey: vaultAuthority, isSigner: false, isWritable: false },
      { pubkey: vaultATA, isSigner: false, isWritable: true },
      ...receiverATAs.map(ata => ({
        pubkey: ata,
        isSigner: false,
        isWritable: true,
      })),
    ])
    .rpc();

  console.log('✅ Tickets purchased:', buyTx);

  // Step 7: Transfer to receivers (BNB immediate transfer)
  console.log('💸 Transferring to receivers...');
  const transferTx = await transferToReceivers(
    params.connection,
    params.buyer,
    buyerATA,
    params.receivers,
    receiverATAs,
    distribution.tokensPerReceiver,
    params.extraTokenMint,
  );

  // Step 8: Transfer to vault (local jackpot)
  console.log('🏆 Transferring to vault...');
  const vaultTx = await transferToVault(
    params.connection,
    params.buyer,
    buyerATA,
    vaultATA,
    vaultAuthority,
    params.extraTokenMint,
    distribution.localJackpot,
  );

  console.log('✅ Lixi created with extra token successfully!');

  return {
    buyTx,
    transferTx,
    vaultTx,
    distribution,
  };
}
